// ---------------------------------------------------------------------------
// music-db.ts — device-local music, remembered across sessions
// ---------------------------------------------------------------------------
//
// Hotel staff add files from their own machine. Those files are never uploaded,
// so what is stored here is a `FileSystemFileHandle` — a durable reference to a
// file on their disk, which IS structured-cloneable and therefore survives in
// IndexedDB. Reopening the app later, the handle is read back and the file is
// reachable again without asking the user to find it.
//
// WHY A SEPARATE DATABASE from core/lib/indexed-db.ts.
//
// That file opens `hotelstack-offline` at version 2 for the offline mutation
// queue, and the service worker keeps its own store in there too. It caches the
// connection forever, never closes it, and has NO `onblocked` and NO
// `onversionchange` handler. Adding a store there means bumping its version —
// and in any tab that already holds the v2 connection, `versionchange` would
// fire with nothing listening, the upgrade would block, and the offline queue
// could wedge. Music is not worth risking the offline queue, so it gets its own
// database and the other one is never touched.
//
// (This is also the established shape here: the theme already lives in a third
// database, `hms-settings`. One database per concern.)
//
// BROWSER SUPPORT is the honest limit. The File System Access API is Chromium
// only. On Firefox and Safari `showOpenFilePicker` does not exist, so local
// tracks cannot be remembered at all — `isLocalFilesSupported()` reports that
// and the UI says so rather than appearing to save and silently forgetting.
// ---------------------------------------------------------------------------

const DB_NAME = 'hotelstack-music';
const DB_VERSION = 1;
const STORE = 'local-tracks';

/**
 * The database the offline queue uses. Named here ONLY so a test can assert we
 * are not it — see music-db.test.ts. Nothing in this file opens it.
 */
export const OFFLINE_DB_NAME_DO_NOT_OPEN = 'hotelstack-offline';

export const MUSIC_DB_NAME = DB_NAME;
export const MUSIC_DB_VERSION = DB_VERSION;
export const MUSIC_STORE_NAME = STORE;

/**
 * The slice of FileSystemFileHandle this feature uses.
 *
 * Declared locally rather than relying on the DOM lib: the permission methods
 * are still behind a draft spec and are missing from some TypeScript lib
 * versions, and `any` would hide exactly the calls that matter.
 */
export interface AudioFileHandle {
  readonly kind: 'file';
  readonly name: string;
  getFile: () => Promise<File>;
  queryPermission?: (descriptor?: { mode?: 'read' | 'readwrite' }) => Promise<PermissionState>;
  requestPermission?: (descriptor?: { mode?: 'read' | 'readwrite' }) => Promise<PermissionState>;
}

export interface LocalTrackRecord {
  /** `local:<uuid>`. Stable, and reused as a playlist `localRef`. */
  id: string;
  handle: AudioFileHandle;
  title: string;
  artist: string | null;
  /** Null until the browser has read metadata off the file. */
  durationSec: number | null;
  /**
   * Size and mtime as they were when added.
   *
   * Lets a changed file be noticed without reading it: if either differs, the
   * user has replaced the file and the cached duration is no longer trustworthy.
   */
  sizeBytes: number;
  lastModified: number;
  addedAt: number;
  lastVerifiedAt: number | null;
}

/**
 * Note what is NOT stored: the verification status.
 *
 * Status is derived each session. Persisting `'missing'` would mean a file
 * restored from a backup still showed as missing until something happened to
 * re-check it, and persisting `'ok'` would claim a file exists that may have
 * been deleted while the app was closed. Neither is knowable without asking the
 * filesystem, so it is always asked.
 */

/** Does this browser support remembering a file across sessions? */
export function isLocalFilesSupported(): boolean {
  return typeof window !== 'undefined' && typeof window.showOpenFilePicker === 'function';
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openMusicDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };

    request.onsuccess = () => {
      const db = request.result;
      // Handled, unlike the offline database: if another tab ever needs to
      // upgrade this one, close so it is not blocked indefinitely. Clearing the
      // cached promise means the next call reopens.
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      resolve(db);
    };

    request.onerror = () => reject(request.error);
    // Fires when another connection is holding an older version open. Surfacing
    // it beats hanging forever with no explanation.
    request.onblocked = () => reject(new Error('Music database upgrade blocked by another tab'));
  });

  // A failed open must not be cached, or one transient error disables local
  // music for the rest of the session.
  dbPromise.catch(() => {
    dbPromise = null;
  });

  return dbPromise;
}

function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openMusicDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE, mode);
        const request = run(transaction.objectStore(STORE));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
        transaction.onerror = () => reject(transaction.error);
      }),
  );
}

export async function putLocalTrack(record: LocalTrackRecord): Promise<void> {
  await tx('readwrite', (store) => store.put(record) as IDBRequest<IDBValidKey>);
}

export async function getLocalTrack(id: string): Promise<LocalTrackRecord | undefined> {
  return tx('readonly', (store) => store.get(id) as IDBRequest<LocalTrackRecord | undefined>);
}

export async function getAllLocalTracks(): Promise<LocalTrackRecord[]> {
  const rows = await tx('readonly', (store) => store.getAll() as IDBRequest<LocalTrackRecord[]>);
  // Newest first, matching how the library lists them.
  return (rows ?? []).sort((a, b) => b.addedAt - a.addedAt);
}

export async function deleteLocalTrack(id: string): Promise<void> {
  await tx('readwrite', (store) => store.delete(id) as IDBRequest<undefined>);
}

export async function clearLocalTracks(): Promise<void> {
  await tx('readwrite', (store) => store.clear() as IDBRequest<undefined>);
}

// TypeScript's DOM lib does not always declare the picker, and it is behind a
// draft spec. Declared narrowly rather than cast to `any` at each call site.
declare global {
  interface Window {
    showOpenFilePicker?: (options?: {
      multiple?: boolean;
      excludeAcceptAllOption?: boolean;
      types?: { description?: string; accept: Record<string, string[]> }[];
    }) => Promise<AudioFileHandle[]>;
  }
}
